// src/utils/authGuards.ts — Guardas puras del flujo de autenticación (POW-02)
// Origen: AUDITORIA_LOGIN_AUTH.md · LOGIN-02 / LOGIN-03 / LOGIN-06.

/**
 * `true` si la URL corresponde a un endpoint de autenticación.
 *
 * En esas rutas un 401 es una respuesta **legítima** del backend
 * (credenciales inválidas, OTP incorrecto) y NO debe disparar la renovación de
 * token ni el cierre de sesión (LOGIN-03). Acepta URL absoluta o relativa.
 */
export function isAuthEndpoint(url?: string): boolean {
  if (!url) return false
  let path = url
  try {
    path = new URL(url, 'http://localhost').pathname
  } catch {
    /* URL relativa simple (sin origen): se usa tal cual */
  }
  return path.startsWith('/api/auth/')
}

/**
 * Normaliza el `detail` de una respuesta de error de FastAPI a un mensaje
 * renderizable. FastAPI emite un `string` (errores de negocio) o un **array**
 * de objetos ValidationError (422); el array nunca debe renderizarse crudo
 * (LOGIN-06: React lanza "Objects are not valid as a React child").
 */
export function normalizeApiDetail(
  detail: unknown,
  fallback = 'Ocurrió un error. Intenta de nuevo.',
): string {
  if (typeof detail === 'string') {
    const trimmed = detail.trim()
    return trimmed.length > 0 ? trimmed : fallback
  }
  if (Array.isArray(detail)) {
    const mensajes = detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && typeof (item as { msg?: unknown }).msg === 'string') {
          return (item as { msg: string }).msg
        }
        return ''
      })
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    return mensajes.length > 0 ? mensajes.join(' ') : fallback
  }
  return fallback
}

/**
 * Endpoints de autenticación **anónima**: en ellos un 401 es una respuesta de
 * negocio (credenciales/OTP inválidos) y NO debe disparar la renovación de token
 * (LOGIN-03). Los endpoints de auth **autenticados** (p. ej.
 * `PUT /api/auth/contrasena`) quedan fuera de la lista para que un access token
 * vencido sí se renueve (BUG-10).
 */
const AUTH_ANONIMOS = [
  '/api/auth/login',
  '/api/auth/login/admin',
  '/api/auth/admin/login',
  '/api/auth/2fa/verificar',
  '/api/auth/admin/verify-2fa',
  '/api/auth/admin/resend-2fa',
  '/api/auth/registro',
  '/api/auth/contrasena/reset',
  '/api/auth/contrasena/confirmar',
]

export function isAnonymousAuthEndpoint(url?: string): boolean {
  if (!url) return false
  let path = url
  try {
    path = new URL(url, 'http://localhost').pathname
  } catch {
    /* URL relativa simple (sin origen): se usa tal cual */
  }
  return AUTH_ANONIMOS.includes(path)
}

/**
 * Destino seguro tras el login (BUG-08): respeta `returnTo` solo si es una ruta
 * interna (evita open-redirect) y cae al `fallback` en cualquier otro caso.
 */
export function destinoInterno(returnTo: string | null | undefined, fallback: string): string {
  if (returnTo && returnTo.startsWith('/') && returnTo !== '/' && !returnTo.startsWith('//')) {
    return returnTo
  }
  return fallback
}

export type AccionAnte401 = 'ignorar' | 'reintentar' | 'refrescar' | 'cerrar-sesion'

/**
 * Decide qué hacer ante un 401 (BUG-17). Aísla la lógica del interceptor en una
 * función pura para poder probarla de verdad (sin mockear axios):
 *
 * - `ignorar`: endpoints de auth anónima → el 401 es la respuesta de negocio.
 * - `cerrar-sesion`: ya se reintentó con el token nuevo, o no hay refresh.
 * - `reintentar`: hay un refresh en curso → encolar la petición.
 * - `refrescar`: caso normal.
 */
export function decidirAnte401(opts: {
  url?: string
  hayRefresh: boolean
  refrescando: boolean
  yaReintentado: boolean
}): AccionAnte401 {
  if (isAnonymousAuthEndpoint(opts.url)) return 'ignorar'
  if (opts.yaReintentado) return 'cerrar-sesion'
  if (opts.refrescando) return 'reintentar'
  if (!opts.hayRefresh) return 'cerrar-sesion'
  return 'refrescar'
}

/**
 * Códigos que devuelve `PUT /api/auth/contrasena` (ver `cambiar_contrasena` y
 * `validate_password_strength` en el backend) traducidos a texto para el modal
 * LOGIN-15. Mostrar el código crudo (p. ej. "contrasena_actual_incorrecta") no
 * le dice nada al administrador.
 */
const ERRORES_CAMBIO_CONTRASENA: Record<string, string> = {
  contrasena_actual_incorrecta: 'La contraseña actual no es correcta.',
  password_muy_corta: 'La nueva contraseña debe tener al menos 8 caracteres.',
  password_sin_mayuscula: 'La nueva contraseña debe incluir al menos una letra mayúscula.',
  password_sin_numero: 'La nueva contraseña debe incluir al menos un número.',
  password_sin_especial: 'La nueva contraseña debe incluir al menos un carácter especial.',
  password_debil: 'La contraseña no cumple con los requisitos de seguridad.',
  usuario_invalido: 'La sesión no es válida. Vuelve a iniciar sesión.',
  token_invalido: 'La sesión no es válida. Vuelve a iniciar sesión.',
}

/** Mensaje legible para el error de cambio de contraseña (LOGIN-15). */
export function mensajeErrorCambioContrasena(detail: unknown): string {
  const fallback = 'No se pudo actualizar la contraseña.'
  if (typeof detail === 'string') {
    const msg = ERRORES_CAMBIO_CONTRASENA[detail.trim()]
    if (msg) return msg
  }
  return normalizeApiDetail(detail, fallback)
}
